import { useState } from 'react';
import { Alert, Button, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { useParams } from 'react-router';
import {
  KURUL_KACINMA_TALEBI_ETIKETI,
  KURUL_TOPLANTI_DURUM_ETIKETI,
  KURUL_TOPLANTI_TIP_ETIKETI,
} from '@isg/validation';
import type { ToplantiDetay } from '@isg/validation';
import { useCiktiIndir, useKurulToplantisi } from '../api/queries';
import {
  GundemBolumu,
  IzBolumu,
  KapanisBolumu,
  KararlarBolumu,
  KatilimBolumu,
  NushaBolumu,
  OncekiKararlarBolumu,
} from './kurul-parts';
import { QueryStatus } from './parts';
import { Bolum, KayitDetayi, Rozet } from '../sozlesme';
import { ANLAM } from '../theme';
import { tarihGoster, zamanGoster } from '../lib/format';

/**
 * TOPLANTI DETAYI (Kapsam §28; sözleşme P5 · P9 · P11). Bölümler alt alta
 * (PDF sayfanın kendisidir): künye · katılım (yeter sayı) · gündem 1
 * (türetilmiş) · gündem · kararlar · kapanış · nüsha · iz.
 * ⚠️ KAPANIŞ DÜĞMESİNİN SEBEBİ YANINDA (G5) — `yetkiler.kapanisEngeli`
 * sunucudan. Kapalı toplantıda yazma denetimleri çizilmez; D9 düzeltme ayrı bayrak.
 */
export function KurulToplantiDetayScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const toplanti = useKurulToplantisi(id);
  const indir = useCiktiIndir();
  const [kapanisAcik, setKapanisAcik] = useState(false);
  if (!toplanti.isSuccess) return <QueryStatus queries={[toplanti]} kayit />;
  const t = toplanti.data;
  return (
    <KayitDetayi
      testId="kurul-toplanti-detay"
      geriYol={`/isg-kurulu/${t.committeeId}`}
      geriEtiket="Kurul"
      baslik={t.takipNo}
      ustBilgi={
        <Group gap="xs">
          <Rozet anlam="bilgi" testId="kurul-toplanti-tip-rozeti">
            {KURUL_TOPLANTI_TIP_ETIKETI[t.tip]}
          </Rozet>
          {/* G3.3 — işaret rozet olarak; tebliğ akışı kapanışta bundan okunur. */}
          {t.kacinmaTalebi && (
            <Rozet anlam="dikkat" testId="kurul-toplanti-kacinma-rozeti">
              {KURUL_KACINMA_TALEBI_ETIKETI}
            </Rozet>
          )}
          <Rozet
            anlam={t.durum === 'planlandi' ? 'dikkat' : t.durum === 'yapildi' ? 'olumlu' : 'engel'}
            testId="kurul-toplanti-durum"
          >
            {KURUL_TOPLANTI_DURUM_ETIKETI[t.durum]}
          </Rozet>
          <Rozet
            anlam={t.yeterSayi.saglandi ? 'olumlu' : 'engel'}
            testId="kurul-toplanti-yeter-sayi"
          >
            Yeter sayı {t.yeterSayi.katilan}/{t.yeterSayi.uyeTamSayisi}
          </Rozet>
        </Group>
      }
      eylemler={
        <Group gap="xs" align="center">
          <Button
            variant="default"
            data-testid="kurul-toplanti-pdf"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({
                yol: `/api/committee-meetings/${t.id}/pdf`,
                yedekAd: `${t.takipNo}.pdf`,
              })
            }
          >
            Tutanak PDF
          </Button>
          {t.durum === 'planlandi' && t.yetkiler.yazabilir && (
            <>
              {t.yetkiler.kapanisEngeli && (
                <Text size="xs" c="dimmed" data-testid="kurul-toplanti-kapanis-engeli">
                  {t.yetkiler.kapanisEngeli}
                </Text>
              )}
              <Button data-testid="kurul-toplanti-kapat" onClick={() => setKapanisAcik((a) => !a)}>
                {kapanisAcik ? 'Kapanışı iptal' : 'Toplantıyı kapat'}
              </Button>
            </>
          )}
        </Group>
      }
    >
      {t.kirkSekizSaatUyarisi && (
        <Alert color={ANLAM.dikkat} data-testid="kurul-toplanti-48-uyarisi">
          Toplantı 48 saatten yakın: yönetmelik üyelere en az 48 saat önce bildirim ister. Davet
          gönderildi; kararı siz verin.
        </Alert>
      )}
      <Kunye t={t} />
      {kapanisAcik && t.durum === 'planlandi' && (
        <KapanisBolumu
          toplanti={t}
          onKapandi={() => setKapanisAcik(false)}
          onVazgec={() => setKapanisAcik(false)}
        />
      )}
      <KatilimBolumu toplanti={t} />
      <OncekiKararlarBolumu toplanti={t} />
      <GundemBolumu toplanti={t} />
      <KararlarBolumu toplanti={t} />
      {t.durum !== 'planlandi' && <NushaBolumu toplanti={t} />}
      <IzBolumu toplanti={t} />
    </KayitDetayi>
  );
}

function Kunye({ t }: { t: ToplantiDetay }): React.JSX.Element {
  return (
    <Bolum baslik="Künye" testId="kurul-toplanti-kunye">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
        <Alan etiket="Tarih" deger={tarihGoster(t.tarih)} />
        <Alan
          etiket="Saat"
          deger={[t.baslangicSaat, t.bitisSaat].filter(Boolean).join(' – ') || '—'}
        />
        <Alan etiket="Yer" deger={t.yer ?? '—'} />
        <Alan etiket="İşyeri" deger={`${t.companyAd} — ${t.workplaceAd}`} />
        <Alan etiket="Planlayan" deger={t.planlayanEtiket} />
        <Alan
          etiket="Kapanış"
          deger={t.kapanisAt ? zamanGoster(t.kapanisAt) : '—'}
          testId="kurul-toplanti-kunye-kapanis"
        />
        <Alan etiket="Kapanış notu" deger={t.kapanisNotu ?? '—'} />
        <Alan etiket="Açık karar" deger={String(t.acikKararSayisi)} />
        <Alan
          etiket="Planlama notu"
          deger={t.planlamaNotu ?? '—'}
          testId="kurul-toplanti-kunye-planlama-notu"
        />
      </SimpleGrid>
    </Bolum>
  );
}

function Alan({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={2} data-testid={testId}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm">{deger}</Text>
    </Stack>
  );
}
